import { Head, Link } from '@inertiajs/react';

import {
    Btn,
    DesignFooter,
    DesignHeader,
    Headshot,
    I,
    Pill,
    Stars,
    UtilityBar,
} from '@/Components/Design';

type Lawyer = {
    id: number;
    slug: string;
    name: string;
    firm: string | null;
    city: string | null;
    state: string | null;
    stateSlug: string | null;
    stateAbbr: string | null;
    phone: string | null;
    email: string | null;
    yearsLicensed: number | null;
    languages: string[];
    practiceAreas: Array<{ slug: string; name: string }>;
    reviewsAvg: number | null;
    reviewsCount: number;
    headshotKey: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    postalCode: string | null;
    firmUrl: string | null;
    yearAdmitted: number | null;
    barNumber: string | null;
    barAdmissions: string[] | null;
    lawSchool: string | null;
    lawSchoolYear: number | null;
    undergrad: string | null;
    undergradYear: number | null;
    bio: string | null;
    awards: Array<{ name: string; year: number | null; issuer: string | null }>;
    reviews: Array<{ author: string; rating: number; body: string; createdAt: string | null; caseType: string | null }>;
    verified: boolean;
    acceptingClients: boolean;
    feeStructure: string | null;
    freeConsultation: boolean;
};

type Props = { lawyer: Lawyer };

function initialsOf(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || 'AN';
}

function hueOf(slug: string): number {
    let h = 0;
    for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
    return Math.abs(h) % 6;
}

function ContactCard({ lawyer }: { lawyer: Lawyer }) {
    return (
        <div data-card style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
            <div style={{ padding: '20px 22px 16px', background: 'var(--paper)', borderBottom: '1px solid var(--rule)' }}>
                <div style={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 8, fontWeight: 600 }}>Contact the attorney</div>
                {lawyer.phone ? (
                    <a className="pip-mono" href={`tel:${lawyer.phone.replace(/[^0-9+]/g, '')}`} style={{ fontSize: 30, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                        <span style={{ color: 'var(--ember)' }}>{I.phone}</span>
                        {lawyer.phone}
                    </a>
                ) : (
                    <div style={{ fontSize: 14, color: 'var(--ink-mute)' }}>Contact via form below</div>
                )}
                {(lawyer.acceptingClients || lawyer.freeConsultation) && (
                    <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 12, color: 'var(--ink-mute)', alignItems: 'center' }}>
                        {lawyer.acceptingClients && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--sage)' }}>
                                <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--sage)' }} />
                                Taking new cases
                            </span>
                        )}
                        {lawyer.acceptingClients && lawyer.freeConsultation && <span>·</span>}
                        {lawyer.freeConsultation && <span>Free consult</span>}
                    </div>
                )}
                {lawyer.firmUrl && (
                    <div style={{ marginTop: 10, fontSize: 12 }}>
                        <a
                            href={lawyer.firmUrl}
                            rel="nofollow noopener noreferrer external"
                            target="_blank"
                            style={{ color: 'var(--ember)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                            Firm website ↗
                        </a>
                    </div>
                )}
            </div>
            <div style={{ padding: 22 }}>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 500, marginBottom: 14 }}>Or send a quick message</div>
                <form action="/get-matched" method="get" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <input name="name" placeholder="Your name" style={inputStyle} />
                    <input name="phone" placeholder="Phone number" style={inputStyle} />
                    <textarea name="description" placeholder="Briefly, what happened? (we keep it private)" rows={3} style={{ ...inputStyle, padding: '12px 14px', resize: 'none', fontFamily: 'inherit' }} />
                    <input type="hidden" name="lawyerSlug" value={lawyer.slug} />
                    <Btn kind="primary" full size="lg" icon={I.arrow} type="submit">
                        Send to attorney
                    </Btn>
                </form>
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--rule-soft)', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-mute)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ display: 'inline-flex', color: 'var(--sage)' }}>{I.check}</span> No obligation
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ display: 'inline-flex', color: 'var(--sage)' }}>{I.check}</span> Private
                    </span>
                </div>
                <div style={{ marginTop: 14, fontSize: 11, color: 'var(--ink-mute)', lineHeight: 1.5, fontStyle: 'italic' }}>
                    Submitting does not create an attorney-client relationship.
                </div>
            </div>
        </div>
    );
}

const inputStyle: React.CSSProperties = {
    height: 46,
    padding: '0 14px',
    border: '1px solid var(--rule)',
    borderRadius: 10,
    background: 'var(--card)',
    fontSize: 14,
    outline: 0,
    fontFamily: 'inherit',
    color: 'var(--ink)',
    width: '100%',
};

export default function LawyerProfile({ lawyer }: Props) {
    const initials = initialsOf(lawyer.name);
    const hue = hueOf(lawyer.slug);
    const headshotUrl = lawyer.headshotKey ? `/cdn/lawyers/640/${lawyer.headshotKey}.webp` : null;
    const stats: [string, string][] = [
        ['Years licensed', lawyer.yearsLicensed ? `${lawyer.yearsLicensed}` : '—'],
        ['Reviews', lawyer.reviewsCount > 0 ? `${lawyer.reviewsCount}` : '—'],
        ['Free consult', lawyer.freeConsultation ? 'Yes' : '—'],
        ['Fee', lawyer.feeStructure ? lawyer.feeStructure : '—'],
    ];

    return (
        <>
            <Head>
                <title>{`${lawyer.name} — Personal injury lawyer in ${lawyer.city ?? ''} ${lawyer.stateAbbr ?? ''}`}</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="Find a Lawyer" />

                <section data-profile-section="hero" style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 56px' }}>
                    {/* Breadcrumb */}
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 24 }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
                        <span>/</span>
                        {lawyer.stateSlug && (
                            <>
                                <Link href={`/${lawyer.stateSlug}`} style={{ textDecoration: 'none', color: 'inherit' }}>{lawyer.state}</Link>
                                <span>/</span>
                            </>
                        )}
                        {lawyer.city && (
                            <>
                                <span>{lawyer.city}</span>
                                <span>/</span>
                            </>
                        )}
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>{lawyer.name}</span>
                    </div></nav>

                    {/* Hero grid */}
                    <div data-profile-hero-grid style={{ display: 'grid', gridTemplateColumns: '1.5fr 420px', gap: 48, marginBottom: 48 }}>
                        <div>
                            <div data-profile-hero-top style={{ display: 'flex', gap: 28, marginBottom: 24 }}>
                                {headshotUrl ? (
                                    <img src={headshotUrl} alt={lawyer.name} style={{ width: 144, height: 144, borderRadius: 16, objectFit: 'cover', flexShrink: 0 }} />
                                ) : (
                                    <Headshot initials={initials} hue={hue} size={144} radius={16} />
                                )}
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <div data-pill-row style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                                        {lawyer.verified && (
                                            <Pill tone="sage">
                                                <span style={{ display: 'inline-flex' }}>{I.check}</span>
                                                Verified by State Bar of {lawyer.state}
                                            </Pill>
                                        )}
                                        {lawyer.acceptingClients && <Pill tone="ember">Taking new cases</Pill>}
                                    </div>
                                    <h1 style={{ fontSize: 52, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 6, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>{lawyer.name}</h1>
                                    <div style={{ fontSize: 17, color: 'var(--ink-soft)' }}>
                                        {lawyer.firm}{lawyer.firm && lawyer.city ? ' · ' : ''}{lawyer.city}{lawyer.city && lawyer.stateAbbr ? ', ' : ''}{lawyer.stateAbbr}
                                    </div>
                                    <div data-profile-meta-row style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 14, fontSize: 13, color: 'var(--ink-soft)', flexWrap: 'wrap' }}>
                                        {lawyer.reviewsCount > 0 && <Stars value={lawyer.reviewsAvg ?? 0} reviews={lawyer.reviewsCount} />}
                                        {lawyer.reviewsCount > 0 && <span style={{ color: 'var(--ink-mute)' }}>·</span>}
                                        {lawyer.yearsLicensed && (
                                            <span>
                                                <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{lawyer.yearsLicensed} years</b> licensed
                                            </span>
                                        )}
                                        {lawyer.languages.length > 0 && (
                                            <>
                                                <span style={{ color: 'var(--ink-mute)' }}>·</span>
                                                <span>{lawyer.languages.join(' · ')}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Stats strip */}
                            <div data-profile-stats style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', border: '1px solid var(--rule)', borderRadius: 12, background: 'var(--card)', overflow: 'hidden' }}>
                                {stats.map(([l, v], i) => (
                                    <div key={l} style={{ padding: '18px 20px', borderLeft: i ? '1px solid var(--rule)' : 0 }}>
                                        <div style={{ fontSize: 11, color: 'var(--ink-mute)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6, fontWeight: 500 }}>{l}</div>
                                        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 500 }}>{v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <ContactCard lawyer={lawyer} />
                        </div>
                    </div>

                    {/* Body grid */}
                    <div data-profile-body-grid style={{ display: 'grid', gridTemplateColumns: '1.5fr 420px', gap: 48, alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
                            {/* About */}
                            {lawyer.bio && (
                                <section>
                                    <h2 style={{ fontSize: 28, marginBottom: 18 }}>About</h2>
                                    <div style={{ fontSize: 17, color: 'var(--ink-soft)', lineHeight: 1.7, maxWidth: 680, whiteSpace: 'pre-wrap' }}>
                                        {lawyer.bio}
                                    </div>
                                </section>
                            )}

                            {/* Practice areas */}
                            {lawyer.practiceAreas.length > 0 && (
                                <section>
                                    <h2 style={{ fontSize: 28, marginBottom: 18 }}>Practice areas</h2>
                                    <div data-profile-pa-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                        {lawyer.practiceAreas.map((pa) => (
                                            <Link key={pa.slug} href={`/${pa.slug}`} style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 18, textDecoration: 'none', color: 'inherit' }}>
                                                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 500 }}>{pa.name}</div>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Credentials — only render if at least one group has real data. */}
                            {(() => {
                                const groups = [
                                    { h: 'Bar admissions', l: [lawyer.barNumber ? `${lawyer.state ?? 'State'} #${lawyer.barNumber}${lawyer.yearAdmitted ? ` (${lawyer.yearAdmitted})` : ''}` : null, ...(lawyer.barAdmissions ?? [])].filter((x): x is string => !!x) },
                                    { h: 'Education', l: [
                                        lawyer.lawSchool ? `J.D., ${lawyer.lawSchool}${lawyer.lawSchoolYear ? ` (${lawyer.lawSchoolYear})` : ''}` : null,
                                        lawyer.undergrad ? `${lawyer.undergrad}${lawyer.undergradYear ? ` (${lawyer.undergradYear})` : ''}` : null,
                                    ].filter((x): x is string => !!x) },
                                    { h: 'Languages', l: lawyer.languages },
                                    { h: 'Awards & memberships', l: lawyer.awards.map((a) => `${a.name}${a.year ? ` (${a.year})` : ''}${a.issuer ? ` — ${a.issuer}` : ''}`) },
                                ].filter((g) => g.l.length > 0);
                                if (groups.length === 0) return null;
                                return (
                                    <section>
                                        <h2 style={{ fontSize: 28, marginBottom: 18 }}>Credentials &amp; education</h2>
                                        <div data-profile-cred-grid style={{ display: 'grid', gridTemplateColumns: groups.length > 1 ? '1fr 1fr' : '1fr', gap: 32, background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 24 }}>
                                            {groups.map((g) => (
                                                <div key={g.h}>
                                                    <div style={{ fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>{g.h}</div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {g.l.map((item, i) => (
                                                            <div key={i} style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.4, paddingLeft: 14, position: 'relative' }}>
                                                                <span style={{ position: 'absolute', left: 0, top: 7, width: 5, height: 5, background: 'var(--ink)', borderRadius: 999 }} />
                                                                {item}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                );
                            })()}

                            {/* Reviews */}
                            {lawyer.reviews.length > 0 && (
                                <section>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 18 }}>
                                        <h2 style={{ fontSize: 28 }}>Reviews</h2>
                                        <span style={{ fontSize: 14, color: 'var(--ink-mute)' }}>{lawyer.reviewsCount} verified</span>
                                    </div>
                                    <div data-profile-reviews-grid style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 32, alignItems: 'flex-start' }}>
                                        <div data-card style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 24 }}>
                                            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 56, fontWeight: 500, lineHeight: 1, letterSpacing: '-0.02em' }}>{(lawyer.reviewsAvg ?? 0).toFixed(1)}</div>
                                            <div style={{ marginTop: 8 }}>
                                                <Stars value={lawyer.reviewsAvg ?? 0} size={16} />
                                            </div>
                                            <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 4 }}>Based on {lawyer.reviewsCount} verified review{lawyer.reviewsCount === 1 ? '' : 's'}</div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {lawyer.reviews.slice(0, 3).map((r, i) => (
                                                <div key={i} data-card style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 20 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            <div style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--paper-deep)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-serif)', fontSize: 15, fontWeight: 600 }}>
                                                                {r.author[0]?.toUpperCase() ?? '?'}
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: 14, fontWeight: 600 }}>{r.author}</div>
                                                                <div style={{ fontSize: 11, color: 'var(--ink-mute)' }}>
                                                                    {r.caseType && `${r.caseType} · `}
                                                                    {r.createdAt && new Date(r.createdAt).toLocaleDateString()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <Stars value={r.rating} size={13} />
                                                    </div>
                                                    <p style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.55 }}>{r.body}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Location */}
                            {lawyer.addressLine1 && (
                                <section>
                                    <h2 style={{ fontSize: 28, marginBottom: 18 }}>Location</h2>
                                    <div data-card style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 12, padding: 24 }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
                                            <span style={{ color: 'var(--ember)', marginTop: 2 }}>{I.pin}</span>
                                            <div>
                                                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 500, lineHeight: 1.3 }}>{lawyer.addressLine1}</div>
                                                {lawyer.addressLine2 && <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>{lawyer.addressLine2}</div>}
                                                <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
                                                    {lawyer.city}, {lawyer.stateAbbr} {lawyer.postalCode}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Disclaimer */}
                            <section style={{ padding: '20px 0 0', borderTop: '1px solid var(--rule)', fontSize: 11, color: 'var(--ink-mute)', lineHeight: 1.7, fontStyle: 'italic' }}>
                                Attorney advertising. This page does not constitute legal advice and does not create an attorney-client relationship. Prior results do not guarantee a similar outcome.
                            </section>
                        </div>
                        <div data-profile-rail />
                    </div>
                </section>

                <DesignFooter />
            </div>
        </>
    );
}
